import { Product } from '@portal/portal-core';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ProductTabActions } from 'actions';
import { FjFlexContainer } from 'components/primitives';
import ProductTab from 'components/product-tab';
import TabsContainer from 'components/tabs-container';
import { currentTabSelector, selectAllProductLicenses, selectCurrentUser } from 'selectors';
import { useAppDispatch } from 'store/hooks';
import { chinaFlag } from '../../utils';
import SelectedTabContent from './selected-tab-content';
import UserAccountToolArea from './user-account-tool-area/index';

// Maps product-name -> cloud-core license objects (untyped boundary).
type ProductLicenses = Record<string, unknown[]>;

const { NAMES_UI_TABS } = Product;
const { flowJo, seqGeq, bdrc, infinicytRuo } = NAMES_UI_TABS;

// Infinicyt RUO is always shown so unlicensed users can see the self-service trial CTA.
const alwaysShowThesePages = [flowJo, seqGeq, bdrc, infinicytRuo];

export const shouldBeHidden = (name: string, licenses: ProductLicenses): boolean => {
  if (chinaFlag()) {
    if (name === bdrc) {
      return true;
    }
    // Self-service trials are disabled in China, so RUO is only shown to licensed users there.
    if (name === infinicytRuo) {
      return !licenses[name]?.length;
    }
  }
  return !alwaysShowThesePages.includes(name) && licenses[name]?.length === 0;
};

const renderProductTabs = (licenses: ProductLicenses): React.ReactElement[] =>
  (Object.values(NAMES_UI_TABS) as string[]).map((name) => (
    <ProductTab key={name} product={name} hidden={shouldBeHidden(name, licenses)} />
  ));

const UserAccount = (): React.ReactElement => {
  const licenses = useSelector(selectAllProductLicenses) as ProductLicenses;
  const activeTab = useSelector(currentTabSelector) as string;
  const currentUser = useSelector(selectCurrentUser) as { id?: string };
  const dispatch = useAppDispatch();

  // A deep link (?productName=) can select a tab that is hidden for this user. Its content is
  // withheld, and once the user sync has landed (users and cost centers arrive together, so
  // licenses are known) the selection falls back to FlowJo. Waiting for the sync keeps a
  // licensed user's deep link from being bounced before their license loads.
  const activeTabHidden = shouldBeHidden(activeTab, licenses);
  const userSynced = Boolean(currentUser?.id);
  useEffect(() => {
    if (userSynced && activeTabHidden) {
      dispatch(ProductTabActions.selectedProductTab(flowJo));
    }
  }, [userSynced, activeTabHidden, dispatch]);

  return (
    <FjFlexContainer>
      <UserAccountToolArea />
      <TabsContainer
        tabs={renderProductTabs(licenses)}
        tabContent={activeTabHidden ? null : <SelectedTabContent />}
      />
    </FjFlexContainer>
  );
};

export default UserAccount;
